import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Chip,
  IconButton, Tooltip, Alert, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, Tab
} from '@mui/material';
import AppLoader from '../../../components/AppLoader';
import {
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Assessment as AssessmentIcon,
  Class as ClassIcon,
  Subject as SubjectIcon,
  Person as PersonIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../contexts/UserContext';
import { fetchJson } from '../../../utils/fetchJson';

const AcademicReportsPage = () => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [hasPermission, setHasPermission] = useState(false);
  const [academicData, setAcademicData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    academicYear: '',
    term: '',
    class: '',
    subject: '',
    reportType: 'overview'
  });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  useEffect(() => {
    const allowed = !!user;
    setHasPermission(allowed);
    if (allowed) loadAcademicData();
  }, [user]);

  const loadAcademicData = async () => {
    let maxGuard;
    try {
      setLoading(true);
      // Prefill from cache scoped by school|phase|ay and filter tuple
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
        const fKey = `${filters.academicYear||'-'}|${filters.term||'-'}|${filters.class||'-'}|${filters.subject||'-'}|${filters.reportType||'overview'}`;
        const cacheKey = `academicReports:data:${ctxKey}|${fKey}`;
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached && typeof cached === 'object') setAcademicData(cached);
        }
      } catch(_) {}

      try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}

      const data = await fetchJson('/school-admin/reports/academic', {
        method: 'POST',
        body: JSON.stringify(filters)
      });
      setAcademicData(data);
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
        const fKey = `${filters.academicYear||'-'}|${filters.term||'-'}|${filters.class||'-'}|${filters.subject||'-'}|${filters.reportType||'overview'}`;
        const cacheKey = `academicReports:data:${ctxKey}|${fKey}`;
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch(_) {}
    } catch (err) {
      setError('Failed to load academic data');
    } finally {
      setLoading(false);
      try { clearTimeout(maxGuard); } catch(_) {}
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    loadAcademicData();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExport = async () => {
    try {
      const response = await fetchJson('/school-admin/reports/academic/export', {
        method: 'POST',
        body: JSON.stringify({ ...filters, format: exportFormat })
      });
      
      // Handle file download
      const blob = new Blob([response.data], { type: `application/${exportFormat}` });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `academic-report-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      link.click();
      setExportDialogOpen(false);
    } catch (error) {
      setError('Failed to export report');
    }
  };

  const getGradeColor = (grade) => {
    if (grade >= 90) return 'success';
    if (grade >= 80) return 'primary';
    if (grade >= 70) return 'warning';
    return 'error';
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <TrendingUpIcon color="success" />;
    if (trend < 0) return <TrendingDownIcon color="error" />;
    return null;
  };

  if (loading) {
    return (
      <AppLoader
        variant="circular3"
        size={60}
        centered
        minHeight="60vh"
        messages={["Loading academic reports","Applying Phase","Applying AY"]}
      />
    );
  }

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" component="h1">
          <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Academic Reports
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={handleApplyFilters}
            sx={{ mr: 1 }}
          >
            Apply Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Report Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={filters.academicYear}
                onChange={(e) => handleFilterChange('academicYear', e.target.value)}
                label="Academic Year"
              >
                <MenuItem value="">All Years</MenuItem>
                <MenuItem value="2024">2024-2025</MenuItem>
                <MenuItem value="2023">2023-2024</MenuItem>
                <MenuItem value="2022">2022-2023</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Term</InputLabel>
              <Select
                value={filters.term}
                onChange={(e) => handleFilterChange('term', e.target.value)}
                label="Term"
              >
                <MenuItem value="">All Terms</MenuItem>
                <MenuItem value="1">Term 1</MenuItem>
                <MenuItem value="2">Term 2</MenuItem>
                <MenuItem value="3">Term 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                value={filters.class}
                onChange={(e) => handleFilterChange('class', e.target.value)}
                label="Class"
              >
                <MenuItem value="">All Classes</MenuItem>
                <MenuItem value="10A">10A</MenuItem>
                <MenuItem value="10B">10B</MenuItem>
                <MenuItem value="9A">9A</MenuItem>
                <MenuItem value="9B">9B</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Subject</InputLabel>
              <Select
                value={filters.subject}
                onChange={(e) => handleFilterChange('subject', e.target.value)}
                label="Subject"
              >
                <MenuItem value="">All Subjects</MenuItem>
                <MenuItem value="mathematics">Mathematics</MenuItem>
                <MenuItem value="science">Science</MenuItem>
                <MenuItem value="english">English</MenuItem>
                <MenuItem value="history">History</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={filters.reportType}
                onChange={(e) => handleFilterChange('reportType', e.target.value)}
                label="Report Type"
              >
                <MenuItem value="overview">Overview</MenuItem>
                <MenuItem value="detailed">Detailed</MenuItem>
                <MenuItem value="comparative">Comparative</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Academic Summary Cards */}
      {academicData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Overall GPA
                </Typography>
                <Typography variant="h4" component="h2">
                  {academicData.overallGPA || '3.8'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  +0.2 from last term
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pass Rate
                </Typography>
                <Typography variant="h4" component="h2">
                  {academicData.passRate || '94%'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Students passing all subjects
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Top Achievers
                </Typography>
                <Typography variant="h4" component="h2">
                  {academicData.topAchievers || '18'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Students with A+ grades
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Improvement Rate
                </Typography>
                <Typography variant="h4" component="h2">
                  {academicData.improvementRate || '76%'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Students showing progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs for Different Report Views */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="academic report tabs">
          <Tab label="Overview" icon={<AssessmentIcon />} />
          <Tab label="Subject Analysis" icon={<SubjectIcon />} />
          <Tab label="Class Performance" icon={<ClassIcon />} />
          <Tab label="Student Rankings" icon={<PersonIcon />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && academicData && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Academic Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Grade Distribution
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {academicData.gradeDistribution?.map((grade) => (
                  <Box key={grade.letter} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={grade.letter} color={getGradeColor(grade.percentage)} size="small" />
                    <Box sx={{ flex: 1, bgcolor: 'grey.200', height: 20, borderRadius: 1 }}>
                      <Box
                        sx={{
                          width: `${grade.percentage}%`,
                          height: '100%',
                          bgcolor: 'primary.main',
                          borderRadius: 1
                        }}
                      />
                    </Box>
                    <Typography variant="body2">{grade.percentage}%</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Subject Performance
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Subject</TableCell>
                    <TableCell>Average</TableCell>
                    <TableCell>Trend</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {academicData.subjectPerformance?.map((subject) => (
                    <TableRow key={subject.name}>
                      <TableCell>{subject.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {subject.average}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getTrendIcon(subject.trend)}
                        <Typography variant="body2" color={subject.trend > 0 ? 'success.main' : 'error.main'}>
                          {subject.trend > 0 ? '+' : ''}{subject.trend}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </Paper>
      )}

      {activeTab === 1 && academicData && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Subject Analysis
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Subject</TableCell>
                <TableCell>Class Average</TableCell>
                <TableCell>Highest Score</TableCell>
                <TableCell>Lowest Score</TableCell>
                <TableCell>Standard Deviation</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {academicData.subjects?.map((subject) => (
                <TableRow key={subject.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SubjectIcon sx={{ mr: 1 }} />
                      {subject.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {subject.average}%
                    </Typography>
                  </TableCell>
                  <TableCell>{subject.highest}%</TableCell>
                  <TableCell>{subject.lowest}%</TableCell>
                  <TableCell>{subject.standardDeviation}</TableCell>
                  <TableCell>
                    <IconButton size="small" title="View Details">
                      <AssessmentIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {activeTab === 2 && academicData && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Class Performance Comparison
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell>Average GPA</TableCell>
                <TableCell>Pass Rate</TableCell>
                <TableCell>Top Performers</TableCell>
                <TableCell>Trend</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {academicData.classes?.map((cls) => (
                <TableRow key={cls.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ClassIcon sx={{ mr: 1 }} />
                      {cls.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {cls.averageGPA}
                    </Typography>
                  </TableCell>
                  <TableCell>{cls.passRate}%</TableCell>
                  <TableCell>{cls.topPerformers}</TableCell>
                  <TableCell>
                    {getTrendIcon(cls.trend)}
                    <Typography variant="body2" color={cls.trend > 0 ? 'success.main' : 'error.main'}>
                      {cls.trend > 0 ? '+' : ''}{cls.trend}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" title="View Details">
                      <AssessmentIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {activeTab === 3 && academicData && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Student Academic Rankings
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>GPA</TableCell>
                <TableCell>Top Subject</TableCell>
                <TableCell>Improvement</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {academicData.studentRankings?.map((student, index) => (
                <TableRow key={student.id}>
                  <TableCell>
                    <Chip
                      label={`#${index + 1}`}
                      color={index < 3 ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      {student.name}
                    </Box>
                  </TableCell>
                  <TableCell>{student.class}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {student.gpa}
                    </Typography>
                  </TableCell>
                  <TableCell>{student.topSubject}</TableCell>
                  <TableCell>
                    {getTrendIcon(student.improvement)}
                    <Typography variant="body2" color={student.improvement > 0 ? 'success.main' : 'error.main'}>
                      {student.improvement > 0 ? '+' : ''}{student.improvement}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" title="View Details">
                      <AssessmentIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Academic Report</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              label="Export Format"
            >
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="excel">Excel</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExport} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AcademicReportsPage;
